import {
  backendFetch,
  type ApiTransport,
  type BackendRequestInit,
} from "@softmax/observatory-client/transport";

const maxInFlight = 6;
let inFlight = 0;
const waiters: Array<() => void> = [];

export async function limitPublicRead<T>(run: () => Promise<T>): Promise<T> {
  // The API allows eight concurrent anonymous requests. Shared public reads
  // use six slots, leaving room for identity and other optional reads.
  if (inFlight >= maxInFlight) {
    await new Promise<void>((resolve) => waiters.push(resolve));
  } else {
    inFlight++;
  }

  try {
    return await run();
  } finally {
    const next = waiters.shift();

    if (next) next();
    else inFlight--;
  }
}

export function publicRead<T>({
  path,
  signal,
}: {
  path: string;
  signal?: AbortSignal | undefined;
}): Promise<T> {
  const options: BackendRequestInit = {
    credentials: "omit",
    headers: { "x-observatory-public-path": "1" },
  };

  if (signal !== undefined) options.signal = signal;

  return limitPublicRead(() =>
    backendFetch<T>("", `/api/observatory${path}`, options),
  );
}

export function publicWatchApi({
  signal,
}: {
  signal?: AbortSignal | undefined;
} = {}): ApiTransport {
  return {
    get: <T>(path: string) => publicRead<T>({ path, signal }),
    post: <T>(path: string, body?: unknown) => {
      const options: BackendRequestInit = {
        method: "POST",
        credentials: "omit",
        headers: { "x-observatory-public-path": "1" },
      };

      if (signal !== undefined) options.signal = signal;

      if (body !== undefined) {
        options.headers = {
          "x-observatory-public-path": "1",
          "Content-Type": "application/json",
        };
        options.body = JSON.stringify(body);
      }

      return limitPublicRead(() =>
        backendFetch<T>("", `/api/observatory${path}`, options),
      );
    },
  };
}
